import React, { FC, useState } from 'react'
import cn from 'classnames'
import Button from '../../components/Button'
import Input from '../../components/Input'
import { IProductTag } from '../../types/products'
import { COLORS } from './tagConstants'
import CheckThin from '../../icons/check-thin.svg'

interface ITagEditorCreateProps {
  mode: 'create'
  initialName?: string
  onSave: (
    name: string,
    color: string,
  ) => Promise<{ errors?: any; result?: IProductTag }>
}

interface ITagEditorEditProps {
  mode: 'edit'
  tag: IProductTag
  onSave: (
    name: string,
    color: string,
  ) => Promise<{ errors?: any; result?: IProductTag }>
  onDelete: () => Promise<void>
}

type ITagEditorProps = ITagEditorCreateProps | ITagEditorEditProps

const TagEditor: FC<ITagEditorProps> = (props) => {
  const startName =
    props.mode === 'edit' ? props.tag.name : props.initialName || ''
  const initialColor = props.mode === 'edit' ? props.tag.color : COLORS[0]

  const [name, setName] = useState(startName)
  const [color, setColor] = useState(initialColor)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)

  const handleSave = async () => {
    if (!name.trim()) {
      setErrors({ name: 'required' })
      return
    }

    setLoading(true)
    try {
      const result = await props.onSave(name.trim(), color)
      if (result?.errors) {
        setErrors(result.errors)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleDeleteClick = async () => {
    if (!confirmDelete) {
      setConfirmDelete(true)
      return
    }

    if (props.mode !== 'edit') return
    setLoading(true)
    try {
      await props.onDelete()
    } catch {
      setConfirmDelete(false)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="tag-editor">
      <div className="tag-editor_field">
        <Input
          label="Label"
          value={name}
          error={errors.name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="tag-editor_colors">
        <div className="tag-editor_color-row">
          {COLORS.map((c) => (
            <button
              key={c}
              type="button"
              className={cn('tag-editor_color-swatch', {
                '-selected': c === color,
              })}
              style={{ backgroundColor: c }}
              onClick={() => setColor(c)}
            >
              {c === color && <CheckThin className="tag-editor_color-check" />}
            </button>
          ))}
        </div>
      </div>
      <div className="tag-editor_actions">
        {props.mode === 'edit' && (
          <div
            className={cn('tag-editor_delete', { '-confirm': confirmDelete })}
            onClick={handleDeleteClick}
          >
            {confirmDelete ? 'Confirm deleting tag?' : 'Delete tag'}
          </div>
        )}
        <Button
          color="primary"
          block
          type="button"
          loading={loading}
          disabled={loading}
          onClick={handleSave}
        >
          {props.mode === 'create' ? 'Add tag' : 'Save'}
        </Button>
      </div>
    </div>
  )
}

export default TagEditor
